import React, { useMemo } from 'react';
import _ from 'lodash';

import { useAITTheme } from '@this/src/components/shared/ui/theme';
import type TransportElement from '@this/domain/transport_element2';
import type OrderItem from '@this/domain/order_item2';
import type { ShinkansenSeatType } from '@this/src/domain/traveler_information';
import { initializeTravelerShinkansenTypes } from './railway_ticket_type_form';

export type ValueType = 'singleTraveler' | 'multipleTravelers' | 'multipleTravelersWithSeatType';

interface Props {
  element: TransportElement;
  orderItem: OrderItem;
  transportIndex: number;
  classNameForModifiedField: (
    pathName: 'shinkansenSeatType' | 'shinkansenTicketType',
    transportIndex: number,
    travelerIndex?: number
  ) => string;
}

export const RailwaySeatTypeForm: React.FC<Props> = ({
  element,
  orderItem,
  transportIndex,
  classNameForModifiedField
}) => {
  const { theme } = useAITTheme();

  const valueType = useMemo((): ValueType => {
    return convertValueType(element.railwaySeatType, orderItem.elements.length);
  }, [element.railwaySeatType, orderItem.elements.length]);

  if (valueType === 'singleTraveler') {
    return (
      <select
        value={element.railwaySeatType || 0}
        onChange={e => {
          element.railwaySeatType = parseInt(e.target.value, 10);
        }}
      >
        <option value={0}>自由席</option>
        <option value={1}>指定席</option>
        <option value={2}>グリーン車</option>
      </select>
    );
  }

  return (
    <>
      {orderItem.travelerInformations.map((ti, travelerIndex) => (
        <div
          key={travelerIndex}
          className={classNameForModifiedField('shinkansenSeatType', transportIndex, travelerIndex)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: theme.tokens.colors.text.placeholder
          }}
        >
          <select
            value={travelerShinkansenSeatType(valueType, orderItem, transportIndex, travelerIndex) || 0}
            onChange={e =>
              setShinkansenSeatTypeFromNumber(
                valueType,
                orderItem,
                transportIndex,
                travelerIndex,
                parseInt(e.target.value, 10)
              )
            }
          >
            <option value={0}>自由席</option>
            <option value={1}>指定席</option>
            <option value={2}>グリーン車</option>
          </select>
          {(travelerIndex > 0 || orderItem.travelerInformations.length > 1) && (
            <span>({ti.lastNameKana ? `${ti.lastNameKana} ${ti.firstNameKana}` : ti.user?.email})</span>
          )}
        </div>
      ))}
    </>
  );
};

export const convertValueType = (seatType: number | undefined, length: number): ValueType => {
  if (seatType) {
    return 'singleTraveler';
  }

  if (length > 1) {
    return 'multipleTravelersWithSeatType';
  }

  return 'multipleTravelers';
};

const travelerShinkansenSeatType = (
  valueType: ValueType,
  orderItem: OrderItem,
  transportIndex: number,
  travelerIndex: number
) => {
  if (orderItem.travelerInformations.length === 0) {
    return null;
  }

  const traveler = _.sortBy(orderItem.travelerInformations, ti => ti.id)[travelerIndex];
  const seatType =
    valueType === 'multipleTravelersWithSeatType'
      ? traveler.travelerInformationShinkansenTypes?.[transportIndex]?.shinkansenSeatType ??
        traveler.shinkansenSeatType
      : traveler.shinkansenSeatType;

  switch (seatType) {
    case 'reserve':
      return 1;
    case 'green':
      return 2;
    default:
      return 0;
  }
};

const setShinkansenSeatTypeFromNumber = (
  valueType: ValueType,
  orderItem: OrderItem,
  transportIndex: number,
  travelerIndex: number,
  value: number
) => {
  if (valueType === 'multipleTravelersWithSeatType') {
    initializeTravelerShinkansenTypes(orderItem);
  }

  const shinkansenSeatTypes: Record<number, ShinkansenSeatType> = {
    0: 'free',
    1: 'reserve',
    2: 'green'
  };
  const shinkansenSeatType = shinkansenSeatTypes[value] || 'free';
  const traveler = _.sortBy(orderItem.travelerInformations, ti => ti.id)[travelerIndex];

  if (valueType === 'multipleTravelersWithSeatType') {
    const shinkansenType = traveler.travelerInformationShinkansenTypes?.[transportIndex];

    if (shinkansenType) {
      shinkansenType.shinkansenSeatType = shinkansenSeatType;
    }
  } else {
    traveler.shinkansenSeatType = shinkansenSeatType;
  }

  app.render();
};
